import React, { createContext, useContext, useState, useEffect } from 'react';

const THEME_KEY = 'app_theme';

export const lightColors = {
  bg: '#f8fafc',
  surface: '#ffffff',
  surfaceRaised: '#ffffff',
  surfaceMuted: '#f1f5f9',
  card: '#ffffff',
  cardBorder: '#dbe7e5',
  border: '#dbe7e5',
  borderStrong: '#b7cac7',
  header: '#ffffff',
  headerBorder: '#dbe7e5',
  text: '#0f172a',
  textInverse: '#ffffff',
  textSub: '#64748b',
  textMuted: '#94a3b8',
  input: '#ffffff',
  inputBorder: '#dbe7e5',
  inputText: '#0f172a',
  sectionLabel: '#94a3b8',
  divider: '#dbe7e5',
  tabBar: '#ffffff',
  tabBarBorder: '#dbe7e5',
  modalBg: '#ffffff',
  skeletonBg: '#dbe7e5',
  overlay: 'rgba(2, 6, 23, 0.58)',
  shadow: '#020617',
  accent: '#14f1d1',
  accentDark: '#0fbfae',
  accentPressed: '#0f9f95',
  accentSoft: '#ccfbf1',
  accentText: '#0f766e',
  secondary: '#020617',
  secondaryRaised: '#0f172a',
  success: '#10b981',
  successSoft: '#dcfce7',
  successText: '#166534',
  warning: '#f59e0b',
  warningSoft: '#fef3c7',
  warningText: '#92400e',
  danger: '#ef4444',
  dangerSoft: '#fee2e2',
  dangerText: '#991b1b',
  info: '#0ea5e9',
  infoSoft: '#e0f2fe',
  infoText: '#0369a1',
};

export const darkColors = {
  bg: '#020617',
  surface: '#0f172a',
  surfaceRaised: '#111c31',
  surfaceMuted: '#1e293b',
  card: '#0f172a',
  cardBorder: '#233147',
  border: '#233147',
  borderStrong: '#334155',
  header: '#0f172a',
  headerBorder: '#233147',
  text: '#f8fafc',
  textInverse: '#020617',
  textSub: '#94a3b8',
  textMuted: '#64748b',
  input: '#111c31',
  inputBorder: '#334155',
  inputText: '#f8fafc',
  sectionLabel: '#64748b',
  divider: '#334155',
  tabBar: '#0f172a',
  tabBarBorder: '#233147',
  modalBg: '#0f172a',
  skeletonBg: '#334155',
  overlay: 'rgba(0, 0, 0, 0.68)',
  shadow: '#000000',
  accent: '#14f1d1',
  accentDark: '#0fbfae',
  accentPressed: '#0f9f95',
  accentSoft: '#134e4a',
  accentText: '#5eead4',
  secondary: '#f8fafc',
  secondaryRaised: '#1e293b',
  success: '#10b981',
  successSoft: '#064e3b',
  successText: '#86efac',
  warning: '#f59e0b',
  warningSoft: '#451a03',
  warningText: '#fcd34d',
  danger: '#ef4444',
  dangerSoft: '#450a0a',
  dangerText: '#fca5a5',
  info: '#38bdf8',
  infoSoft: '#082f49',
  infoText: '#7dd3fc',
};

export type ThemeColors = typeof lightColors;

interface ThemeContextType {
  isDark: boolean;
  colors: ThemeColors;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDark: false,
  colors: lightColors,
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(THEME_KEY) === 'dark') setIsDark(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.body.style.background = isDark ? darkColors.bg : lightColors.bg;
    document.body.style.color = isDark ? darkColors.text : lightColors.text;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? darkColors.bg : lightColors.bg);
  }, [isDark]);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    try {
      localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
    } catch {
      /* ignore */
    }
  };

  return (
    <ThemeContext.Provider value={{ isDark, colors: isDark ? darkColors : lightColors, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
